/**
 * HVAC Template Type Definitions
 *
 * Type definitions for HVAC system templates, parameters, and application wizard.
 */

import type { PlantLoopType } from '../plant/types';

/**
 * Template category for organization
 */
export type TemplateCategory =
  | 'vav'
  | 'packaged'
  | 'vrf'
  | 'radiant'
  | 'residential'
  | 'advanced';

/**
 * Building type for applicability filtering
 */
export type BuildingType =
  | 'office'
  | 'retail'
  | 'hotel'
  | 'education'
  | 'healthcare'
  | 'residential'
  | 'warehouse'
  | 'restaurant'
  | 'laboratory';

/**
 * Climate zone for applicability filtering
 */
export type ClimateZone =
  | '1A' | '1B'
  | '2A' | '2B'
  | '3A' | '3B' | '3C'
  | '4A' | '4B' | '4C'
  | '5A' | '5B' | '5C'
  | '6A' | '6B'
  | '7'
  | '8';

/**
 * Parameter type for template configuration
 */
export type TemplateParameterType = 'number' | 'select' | 'boolean' | 'text';

/**
 * Parameter validation rules
 */
export interface ParameterValidation {
  min?: number;
  max?: number;
  required?: boolean;
  pattern?: string;
}

/**
 * Select option for parameter
 */
export interface ParameterOption {
  label: string;
  value: string | number | boolean;
}

/**
 * Template parameter definition
 */
export interface TemplateParameter {
  /** Unique parameter ID */
  id: string;
  /** Display name */
  name: string;
  /** Description/help text */
  description: string;
  /** Parameter type */
  type: TemplateParameterType;
  /** Default value */
  defaultValue: string | number | boolean;
  /** Options for select type */
  options?: ParameterOption[];
  /** Validation rules */
  validation?: ParameterValidation;
  /** Unit of measurement */
  unit?: string;
  /** Group/section for UI organization */
  group?: string;
  /** Conditional visibility expression */
  visibleWhen?: string;
}

/**
 * Template component definition
 */
export interface TemplateComponent {
  /** Unique ID within template */
  id: string;
  /** Component type (references HVAC_COMPONENTS) */
  componentType: string;
  /** Name pattern (can include {parameter} placeholders) */
  namePattern: string;
  /** Relative position in the graph */
  position: { x: number; y: number };
  /** Field overrides/defaults */
  fields?: Record<string, string>;
  /** Conditional inclusion expression */
  conditional?: string;
  /** Loop side for zone equipment */
  loopSide?: 'supply' | 'demand';
}

/**
 * Template connection definition
 */
export interface TemplateConnection {
  /** Source component ID */
  sourceId: string;
  /** Source port ID */
  sourcePort: string;
  /** Target component ID */
  targetId: string;
  /** Target port ID */
  targetPort: string;
  /** Conditional inclusion expression */
  conditional?: string;
}

/**
 * Plant loop template for automatic creation
 */
export interface PlantLoopTemplate {
  /** Loop type */
  type: PlantLoopType;
  /** Equipment type to add (e.g., 'boiler-hot-water', 'chiller-electric') */
  equipment: string;
  /** Sizing method */
  sizing: 'auto' | 'manual';
  /** Conditional creation expression */
  conditional?: string;
  /** Custom name pattern */
  namePattern?: string;
}

/**
 * Control strategy for template
 */
export interface ControlStrategy {
  /** Control type */
  type: string;
  /** Strategy parameters */
  parameters: Record<string, string | number>;
}

/**
 * Applicability filter for template
 */
export interface TemplateApplicability {
  /** Suitable building types */
  buildingTypes: BuildingType[];
  /** Suitable climate zones */
  climateZones: ClimateZone[];
  /** Building size range (m²) */
  sizeRange: { min: number; max: number };
}

/**
 * Zone terminal configuration within template
 */
export interface ZoneTerminalConfig {
  /** Terminal component type */
  componentType: string;
  /** Whether this terminal type includes reheat */
  hasReheat: boolean;
  /** Default fields for terminal */
  fields?: Record<string, string>;
}

/**
 * Complete HVAC template definition
 */
export interface HVACTemplate {
  /** Unique template ID */
  id: string;
  /** Display name */
  name: string;
  /** Detailed description */
  description: string;
  /** Category for organization */
  category: TemplateCategory;
  /** Applicability filter */
  applicability: TemplateApplicability;
  /** Thumbnail image path or icon */
  thumbnail: string;
  /** Air-side components */
  components: TemplateComponent[];
  /** Connections between components */
  connections: TemplateConnection[];
  /** Control strategies */
  controls: ControlStrategy[];
  /** Plant loops to create */
  plantLoops?: PlantLoopTemplate[];
  /** Configurable parameters */
  parameters: TemplateParameter[];
  /** Available terminal types for zones */
  terminalTypes?: ZoneTerminalConfig[];
  /** Whether zones are required */
  requiresZones: boolean;
  /** Minimum number of zones */
  minZones?: number;
  /** Maximum number of zones */
  maxZones?: number;
}

/**
 * Zone selection for template application
 */
export interface ZoneSelection {
  /** Zone name from IDF */
  zoneName: string;
  /** Zone ID from IDF */
  zoneId: string;
  /** Whether this zone is selected */
  selected: boolean;
  /** Terminal type for this zone (if multiple available) */
  terminalType?: string;
  /** Zone-specific overrides */
  overrides?: Record<string, string>;
}

/**
 * Wizard step identifier
 */
export type WizardStep =
  | 'select-template'
  | 'configure-system'
  | 'assign-zones'
  | 'plant-loops'
  | 'review';

/**
 * Wizard state for template application
 */
export interface TemplateWizardState {
  /** Current wizard step */
  currentStep: WizardStep;
  /** Selected template */
  selectedTemplate: HVACTemplate | null;
  /** Parameter values */
  parameterValues: Record<string, string | number | boolean>;
  /** Zone selections */
  zoneSelections: ZoneSelection[];
  /** Plant loop configurations */
  plantLoopConfigs: Record<string, Partial<PlantLoopTemplate>>;
  /** Validation errors */
  errors: string[];
  /** Whether wizard is complete */
  isComplete: boolean;
}

/**
 * Generated system from template
 */
export interface GeneratedSystem {
  /** Generated nodes */
  nodes: Array<{
    id: string;
    componentType: string;
    name: string;
    position: { x: number; y: number };
    fields: Record<string, string>;
  }>;
  /** Generated edges */
  edges: Array<{
    sourceId: string;
    sourcePort: string;
    targetId: string;
    targetPort: string;
  }>;
  /** Generated plant loops */
  plantLoops: Array<{
    type: PlantLoopType;
    name: string;
    equipment: string;
  }>;
}

/**
 * Template application result
 */
export interface TemplateApplicationResult {
  success: boolean;
  /** IDs of created nodes */
  nodeIds: string[];
  /** IDs of created edges */
  edgeIds: string[];
  /** IDs of created plant loops */
  plantLoopIds: string[];
  /** Error message if failed */
  error?: string;
  /** Undo action data */
  undoData: {
    nodeIds: string[];
    edgeIds: string[];
    plantLoopIds: string[];
  };
}

/**
 * Category display information
 */
export const TEMPLATE_CATEGORY_INFO: Record<TemplateCategory, { name: string; description: string; icon: string }> = {
  vav: {
    name: 'VAV Systems',
    description: 'Variable Air Volume systems for commercial buildings',
    icon: 'wind',
  },
  packaged: {
    name: 'Packaged Systems',
    description: 'Self-contained rooftop and split systems',
    icon: 'box',
  },
  vrf: {
    name: 'VRF Systems',
    description: 'Variable Refrigerant Flow multi-zone systems',
    icon: 'snowflake',
  },
  radiant: {
    name: 'Radiant Systems',
    description: 'Radiant heating/cooling with dedicated outdoor air',
    icon: 'sun',
  },
  residential: {
    name: 'Residential',
    description: 'Home heating and cooling systems',
    icon: 'home',
  },
  advanced: {
    name: 'Advanced Systems',
    description: 'Specialized and hybrid system configurations',
    icon: 'settings',
  },
};

/**
 * Wizard step information
 */
export const WIZARD_STEPS: Array<{ id: WizardStep; name: string; description: string }> = [
  {
    id: 'select-template',
    name: 'Select Template',
    description: 'Choose an HVAC system template',
  },
  {
    id: 'configure-system',
    name: 'Configure System',
    description: 'Set system parameters and options',
  },
  {
    id: 'assign-zones',
    name: 'Assign Zones',
    description: 'Select zones to serve',
  },
  {
    id: 'plant-loops',
    name: 'Plant Loops',
    description: 'Configure heating and cooling plants',
  },
  {
    id: 'review',
    name: 'Review',
    description: 'Preview and apply the system',
  },
];
